import log from '@/lib/logger';
import {useEffect, useState} from "react";
import {useForm, useWatch} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {z} from "zod";
import {Loader2, AlertTriangle} from "lucide-react";
import {useTranslation} from "react-i18next";
import {OrderTime, OrderTimeFormData} from "@/models/order-time";
import {Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle} from "@/components/tailwind/dialog";
import {Field, FieldGroup, Fieldset, Label} from "@/components/tailwind/fieldset";
import {Input} from "@/components/tailwind/input";
import {Button} from "@/components/tailwind/button";
import ValidationAlert from "@/components/validation-alert";
import ConfirmationDialog from "@/components/confirmation-dialog/confirmation-dialog";
import {formatDateString} from "@/lib/utils";

const orderTimeSchema = z.object({
    date: z.string().min(1, "orderDetail.times.form.errors.date-required"),
    startTime: z.string().min(1, "orderDetail.times.form.errors.start-time-required"),
    endTime: z.string().min(1, "orderDetail.times.form.errors.end-time-required")
}).refine((data) => {
    if (data.startTime && data.endTime) {
        return data.endTime > data.startTime;
    }
    return true;
}, {
    message: "orderDetail.times.form.errors.end-time-before-start-time",
    path: ["endTime"]
});

interface OrderTimeDialogProps {
    isOpen: boolean;
    orderTime?: OrderTime;
    orderDate: string; // For default date setting
    latestOrderTimeDate?: string; // For subsequent bookings
    onSave: (formData: OrderTimeFormData) => Promise<void>;
    onClose: () => void;
}

export function OrderTimeDialog({
    isOpen,
    orderTime,
    orderDate,
    latestOrderTimeDate,
    onSave,
    onClose
}: OrderTimeDialogProps) {
    const {t} = useTranslation("orders");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serverError, setServerError] = useState<string | null>(null);
    const [showDateConfirmation, setShowDateConfirmation] = useState(false);
    const [pendingFormData, setPendingFormData] = useState<OrderTimeFormData | null>(null);

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        control,
        formState: {errors}
    } = useForm<OrderTimeFormData>({
        resolver: zodResolver(orderTimeSchema)
    });

    const watchedDate = useWatch({
        control,
        name: "date"
    });

    const isEditing = !!orderTime;

    useEffect(() => {
        if (isOpen) {
            if (isEditing && orderTime) {
                // Pre-populate form for editing
                setValue("date", orderTime.date);
                setValue("startTime", orderTime.startTime);
                setValue("endTime", orderTime.endTime);
            } else {
                // Set default date for new order time
                const defaultDate = latestOrderTimeDate || orderDate;
                setValue("date", defaultDate);
                setValue("startTime", "");
                setValue("endTime", "");
            }
        }
    }, [isOpen, isEditing, orderTime, orderDate, latestOrderTimeDate, setValue]);

    const handleClose = () => {
        reset();
        setIsSubmitting(false);
        setServerError(null);
        onClose();
    };

    const onSubmit = async (data: OrderTimeFormData) => {
        // Check if date is before order date and show confirmation
        if (isDateBeforeOrderDate(data.date)) {
            setPendingFormData(data);
            setShowDateConfirmation(true);
            return;
        }
        
        await saveOrderTime(data);
    };

    const saveOrderTime = async (data: OrderTimeFormData) => {
        setIsSubmitting(true);
        setServerError(null);
        try {
            await onSave(data);
            handleClose();
        } catch (error) {
            log.error("Failed to save order time:", error);
            setServerError(t("orderDetail.times.form.errors.server-error"));
            setIsSubmitting(false);
        }
    };

    const handleDateConfirmation = async () => {
        if (pendingFormData) {
            setShowDateConfirmation(false);
            await saveOrderTime(pendingFormData);
            setPendingFormData(null);
        }
    };

    const handleDateConfirmationCancel = () => {
        setShowDateConfirmation(false);
        setPendingFormData(null);
    };

    const isDateBeforeOrderDate = (dateStr: string) => {
        return dateStr < orderDate;
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} size="lg">
            <DialogTitle>
                {isEditing 
                    ? t("orderDetail.times.dialog.edit.title")
                    : t("orderDetail.times.dialog.create.title")
                }
            </DialogTitle>
            <DialogDescription>
                {isEditing 
                    ? t("orderDetail.times.dialog.edit.description")
                    : t("orderDetail.times.dialog.create.description")
                }
            </DialogDescription>
            

            <form onSubmit={handleSubmit(onSubmit)}>
                <DialogBody>
                    <Fieldset>
                        <FieldGroup>
                            <Field>
                                <Label htmlFor="date">
                                    {t("orderDetail.times.form.date.label")}
                                </Label>
                                
                                {/* Information hint if date is before order date */}
                                {watchedDate && isDateBeforeOrderDate(watchedDate) && (
                                    <div className="mb-2 bg-blue-50 dark:bg-blue-950 border-l-4 border-blue-400 dark:border-blue-500 p-3 rounded-r">
                                        <div className="flex items-center">
                                            <AlertTriangle className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2 flex-shrink-0" />
                                            <p className="text-xs text-blue-700 dark:text-blue-300">
                                                {t("orderDetail.times.form.warnings.date-before-order-date")}
                                            </p>
                                        </div>
                                    </div>
                                )}
                                
                                <Input
                                    {...register("date")}
                                    type="date"
                                    id="date"
                                    data-testid="order-time-date"
                                    invalid={!!errors.date}
                                />
                                {errors.date && (
                                    <ValidationAlert message={t(errors.date.message as any) || ""} />
                                )}
                            </Field>

                            <Field>
                                <Label htmlFor="startTime">
                                    {t("orderDetail.times.form.startTime.label")}
                                </Label>
                                <Input
                                    {...register("startTime")}
                                    type="time"
                                    id="startTime"
                                    data-testid="order-time-start-time"
                                    invalid={!!errors.startTime}
                                />
                                {errors.startTime && (
                                    <ValidationAlert message={t(errors.startTime.message as any) || ""} />
                                )}
                            </Field>

                            <Field>
                                <Label htmlFor="endTime">
                                    {t("orderDetail.times.form.endTime.label")}
                                </Label>
                                <Input
                                    {...register("endTime")}
                                    type="time"
                                    id="endTime"
                                    data-testid="order-time-end-time"
                                    invalid={!!errors.endTime}
                                />
                                {errors.endTime && (
                                    <ValidationAlert message={t(errors.endTime.message as any) || ""} />
                                )}
                            </Field>
                        </FieldGroup>
                    </Fieldset>

                    {/* Server error display */}
                    {serverError && (
                        <ValidationAlert 
                            message={serverError}
                            className="mt-4"
                        />
                    )}
                    
                    {/* Metadata display for existing order times */}
                    {isEditing && orderTime && (
                        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                                <div>
                                    {t("orderDetail.times.dialog.metadata.created", {
                                        name: orderTime.metadata.createdByName,
                                        date: formatDateString(orderTime.metadata.createdAt)
                                    })}
                                </div>
                                {orderTime.metadata.updatedByName && orderTime.metadata.updatedAt && (
                                    <div>
                                        {t("orderDetail.times.dialog.metadata.updated", {
                                            name: orderTime.metadata.updatedByName,
                                            date: formatDateString(orderTime.metadata.updatedAt)
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </DialogBody>

                <DialogActions>
                    <Button 
                        type="button" 
                        plain 
                        onClick={handleClose} 
                        disabled={isSubmitting}
                        data-testid="order-time-cancel"
                    >
                        {t("orderDetail.times.dialog.buttons.cancel")}
                    </Button>
                    <Button 
                        type="submit" 
                        disabled={isSubmitting}
                        data-testid="order-time-save"
                    >
                        {isSubmitting ? (
                            <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                {t("orderDetail.times.dialog.buttons.saving")}
                            </>
                        ) : (
                            t("orderDetail.times.dialog.buttons.save")
                        )}
                    </Button>
                </DialogActions>
            </form>
            
            {/* Date confirmation dialog */}
            {pendingFormData && (
                <ConfirmationDialog
                    isOpen={showDateConfirmation}
                    onConfirm={handleDateConfirmation}
                    onCancel={handleDateConfirmationCancel}
                    config={{
                        title: "orderDetail.times.form.confirmation.date-before-order.title",
                        description: "orderDetail.times.form.confirmation.date-before-order.description",
                        descriptionValues: {
                            date: formatDateString(pendingFormData.date),
                            orderDate: formatDateString(orderDate)
                        },
                        cancelButton: {
                            text: "orderDetail.times.form.confirmation.date-before-order.buttons.cancel",
                            ariaLabel: "orderDetail.times.form.confirmation.date-before-order.buttons.cancel"
                        },
                        confirmButton: {
                            text: "orderDetail.times.form.confirmation.date-before-order.buttons.confirm",
                            ariaLabel: "orderDetail.times.form.confirmation.date-before-order.buttons.confirm",
                            color: "blue"
                        }
                    }}
                    namespace="orders"
                    testIdPrefix="order-time-date-confirmation"
                />
            )}
        </Dialog>
    );
}